import { isStandardPalletType } from '../../constants/pallets';
import { v4 as uuidv4 } from 'uuid';
import { emptyToNull, toNumberOrNull } from '../payloadHelpers';
import { DIMENSION_UNITS, SKU_UNITS, WEIGHT_UNITS } from './constants';
import {
  CostPrice,
  Pallet,
  ProductSkusFormValues,
  SkuCardValues,
  SKUFormValues,
} from './types';

const fileOrNull = (value: unknown): File | null =>
  value instanceof File ? value : null;

const withoutBlankCustomFields = (
  customFields: SkuCardValues['customFields']
): SkuCardValues['customFields'] =>
  customFields.filter(
    (field) =>
      (field.label ?? '').trim() !== '' ||
      String(field.value ?? '').trim() !== ''
  );

const optionValue = (option?: { value?: string } | null): string | null =>
  emptyToNull(option?.value ?? '');

const withoutNullValues = <T extends Record<string, unknown>>(
  obj: T
): Partial<T> =>
  Object.entries(obj).reduce((pruned, [key, value]) => {
    if (value === null || value === undefined) return pruned;
    return { ...pruned, [key]: value };
  }, {} as Partial<T>);

export const emptySkuCard = (seed?: {
  skuIdPrefix?: string;
  name?: string;
  asAdditional?: boolean;
}): SkuCardValues => ({
  inventorySkuId: '',
  skuId: seed?.skuIdPrefix ?? '',
  name: seed?.name ?? '',
  barcode: '',
  unitOfMeasure: seed?.asAdditional ? SKU_UNITS[1] : SKU_UNITS[0],
  amountOfProductInSku: seed?.asAdditional ? '' : '1',
  lowStockThreshold: '',
  description: '',
  dimensions: {
    dimensionsUnitOfMeasure: DIMENSION_UNITS[1],
    length: '',
    width: '',
    height: '',
  },
  grossWeightUnitOfMeasure: WEIGHT_UNITS[1],
  grossWeight: '',
  suppliers: [],
  image: null,
  barcodeImage: null,
  skuImage: null,
  skuBarcodeImage: null,
  deletedSKUImageId: '',
  deletedBarcodeImageId: '',
  palletising: [],
  temperatureControlRequirements: null,
  costPrices: [],
  listPrices: [],
  customFields: [],
});

export const duplicateSkuCard = (card: SkuCardValues): SkuCardValues => ({
  ...card,
  inventorySkuId: '',
  skuId: '',
  image: null,
  barcodeImage: null,
  deletedSKUImageId: '',
  deletedBarcodeImageId: '',
  unitOfMeasure: { ...card.unitOfMeasure },
  dimensions: { ...card.dimensions },
  grossWeightUnitOfMeasure: { ...card.grossWeightUnitOfMeasure },
  suppliers: card.suppliers.map((supplier) => ({ ...supplier })),
  palletising: card.palletising.map((pallet) => ({
    ...pallet,
    palletId: uuidv4(),
  })),
  temperatureControlRequirements: card.temperatureControlRequirements
    ? { ...card.temperatureControlRequirements }
    : null,
  costPrices: card.costPrices.map((price) => ({ ...price })),
  listPrices: card.listPrices.map((price) => ({ ...price })),
  customFields: card.customFields.map((field) => ({ ...field })),
});

export const toSkuCard = (flat: SKUFormValues): SkuCardValues => ({
  inventorySkuId: flat.inventorySkuId ?? '',
  skuId: flat.skuId ?? '',
  name: flat.name ?? '',
  barcode: flat.barcode ?? '',
  unitOfMeasure: flat.unitOfMeasure,
  amountOfProductInSku: flat.amountOfProductInSku ?? '',
  lowStockThreshold: flat.lowStockThreshold ?? '',
  description: flat.description ?? '',
  dimensions: flat.dimensions,
  grossWeightUnitOfMeasure: flat.grossWeightUnitOfMeasure,
  grossWeight: flat.grossWeight ?? '',
  suppliers: flat.suppliers ?? [],
  image: flat.image,
  barcodeImage: flat.barcodeImage,
  skuImage: fileOrNull(flat.skuImage),
  skuBarcodeImage: fileOrNull(flat.skuBarcodeImage),
  deletedSKUImageId: flat.deletedSKUImageId ?? '',
  deletedBarcodeImageId: flat.deletedBarcodeImageId ?? '',
  palletising: flat.palletising ?? [],
  temperatureControlRequirements: flat.temperatureControlRequirements,
  costPrices: flat.costPrices ?? [],
  listPrices: flat.listPrices ?? [],
  customFields: flat.customFields ?? [],
});

export const regroupToFormShape = (
  flat: SKUFormValues
): ProductSkusFormValues => ({
  productId: flat.productId ?? '',
  productName: flat.productName ?? '',
  productDescription: flat.productDescription ?? '',
  productBrand: flat.productBrand ?? '',
  productCategory: flat.productCategory ?? '',
  productSubCategory: flat.productSubCategory ?? '',
  productNetVolume: flat.productNetVolume ?? '',
  productNetVolumeUnitOfMeasure: flat.productNetVolumeUnitOfMeasure,
  productBarcode: flat.productBarcode ?? '',
  productImage: flat.productImage,
  skuProductImage: fileOrNull(flat.skuProductImage),
  deletedProductImageId: flat.deletedProductImageId ?? '',
  productBarcodeImage: flat.productBarcodeImage,
  skuProductBarcodeImage: fileOrNull(flat.skuProductBarcodeImage),
  deletedProductBarcodeImageId: flat.deletedProductBarcodeImageId ?? '',
  skus: [toSkuCard(flat)],
});

export const flattenCard = (
  values: ProductSkusFormValues,
  index: number
): SKUFormValues => {
  const card = values.skus[index];
  return {
    productId: values.productId ?? '',
    productName: values.productName ?? '',
    productDescription: values.productDescription ?? '',
    productBrand: values.productBrand ?? '',
    productCategory: values.productCategory ?? '',
    productSubCategory: values.productSubCategory ?? '',
    productNetVolume: values.productNetVolume ?? '',
    productNetVolumeUnitOfMeasure: values.productNetVolumeUnitOfMeasure,
    productBarcode: values.productBarcode ?? '',
    productImage: values.productImage,
    skuProductImage: fileOrNull(values.skuProductImage),
    deletedProductImageId: values.deletedProductImageId ?? '',
    productBarcodeImage: values.productBarcodeImage,
    skuProductBarcodeImage: fileOrNull(values.skuProductBarcodeImage),
    deletedProductBarcodeImageId: values.deletedProductBarcodeImageId ?? '',
    inventorySkuId: card.inventorySkuId ?? '',
    skuId: card.skuId,
    name: card.name,
    barcode: card.barcode ?? '',
    unitOfMeasure: card.unitOfMeasure,
    amountOfProductInSku: card.amountOfProductInSku,
    lowStockThreshold: card.lowStockThreshold ?? '',
    description: card.description ?? '',
    dimensions: card.dimensions,
    grossWeightUnitOfMeasure: card.grossWeightUnitOfMeasure,
    grossWeight: card.grossWeight,
    suppliers: card.suppliers,
    image: card.image,
    barcodeImage: card.barcodeImage,
    skuImage: fileOrNull(card.skuImage),
    skuBarcodeImage: fileOrNull(card.skuBarcodeImage),
    deletedSKUImageId: card.deletedSKUImageId ?? '',
    deletedBarcodeImageId: card.deletedBarcodeImageId ?? '',
    palletising: card.palletising,
    temperatureControlRequirements: card.temperatureControlRequirements,
    costPrices: card.costPrices,
    listPrices: card.listPrices,
    customFields: withoutBlankCustomFields(card.customFields),
  };
};

const toPayloadPallet = (pallet: Pallet) => {
  const declaresItsOwnDimensions = !isStandardPalletType(pallet.type);
  return withoutNullValues({
    id: pallet.palletId || null,
    type: pallet.type,
    amountOfSkusInLayer: toNumberOrNull(pallet.amountOfSkusInLayer),
    amountOfLayersInPallet: toNumberOrNull(pallet.amountOfLayersInPallet),
    ...(declaresItsOwnDimensions
      ? {
          customPalletSpecification: emptyToNull(
            pallet.customPalletSpecification ?? ''
          ),
          customPalletWeight: toNumberOrNull(pallet.customPalletWeight ?? ''),
          customPalletWeightUnit: optionValue(pallet.customPalletWeightUnit),
          dimensions: withoutNullValues({
            dimensionsUnitOfMeasure: optionValue(
              pallet.dimensionsUnitOfMeasure
            ),
            length: toNumberOrNull(pallet.length ?? ''),
            width: toNumberOrNull(pallet.width ?? ''),
            height: toNumberOrNull(pallet.height ?? ''),
          }),
        }
      : {}),
  });
};

const toPayloadPrices = (prices: CostPrice[]) =>
  prices
    .filter((price) => price.amount !== '' || price.comment)
    .map((price) =>
      withoutNullValues({
        amount: toNumberOrNull(price.amount),
        currency: optionValue(price.currency),
        comment: emptyToNull(price.comment ?? ''),
      })
    );

const toPayloadTemperatureControl = (
  requirements: SkuCardValues['temperatureControlRequirements']
) => {
  if (!requirements?.temperatureControlType?.value) return null;
  const unitOfMeasure = optionValue(requirements.unitOfMeasure);
  const toTemperature = (temperature: { quantity: string } | null) => {
    const quantity = toNumberOrNull(temperature?.quantity ?? '');
    if (quantity === null) return null;
    return withoutNullValues({ quantity, unitOfMeasure });
  };
  return withoutNullValues({
    temperatureControlType: requirements.temperatureControlType.value,
    minimumTemperature: toTemperature(requirements.minimumTemperature),
    maximumTemperature: toTemperature(requirements.maximumTemperature),
  });
};

const toPayloadSku = (card: SkuCardValues) =>
  withoutNullValues({
    skuId: emptyToNull(card.skuId),
    name: emptyToNull(card.name),
    unitOfMeasure: optionValue(card.unitOfMeasure),
    amountOfProductInSku: toNumberOrNull(card.amountOfProductInSku) ?? 1,
    lowStockThreshold: toNumberOrNull(card.lowStockThreshold ?? ''),
    barcode: emptyToNull(card.barcode ?? ''),
    description: emptyToNull(card.description ?? ''),
    dimensions: withoutNullValues({
      dimensionsUnitOfMeasure: optionValue(
        card.dimensions.dimensionsUnitOfMeasure
      ),
      length: toNumberOrNull(card.dimensions.length),
      width: toNumberOrNull(card.dimensions.width),
      height: toNumberOrNull(card.dimensions.height),
    }),
    grossWeight: toNumberOrNull(card.grossWeight),
    grossWeightUnitOfMeasure: optionValue(card.grossWeightUnitOfMeasure),
    palletising: card.palletising.map(toPayloadPallet),
    suppliers: card.suppliers.map((supplier) => ({
      supplierId: supplier.value,
      name: supplier.label,
    })),
    metaData: withoutBlankCustomFields(card.customFields).map((field) => ({
      name: field.label,
      value: field.value,
      type: field.type,
    })),
    temperatureControlRequirements: toPayloadTemperatureControl(
      card.temperatureControlRequirements
    ),
    costPrices: toPayloadPrices(card.costPrices),
    listPrices: toPayloadPrices(card.listPrices),
  });

const toPayloadProduct = (values: ProductSkusFormValues) =>
  withoutNullValues({
    productId: emptyToNull(values.productId ?? ''),
    name: emptyToNull(values.productName ?? ''),
    description: emptyToNull(values.productDescription ?? ''),
    brand: emptyToNull(values.productBrand ?? ''),
    category: emptyToNull(values.productCategory ?? ''),
    subCategory: emptyToNull(values.productSubCategory ?? ''),
    netVolume: toNumberOrNull(values.productNetVolume ?? ''),
    netVolumeUnitOfMeasure: optionValue(values.productNetVolumeUnitOfMeasure),
    barcode: emptyToNull(values.productBarcode ?? ''),
  });

export type CreateProductWithSkusRequest = {
  payload: {
    product: Record<string, unknown>;
    skus: Record<string, unknown>[];
  };
  files: { name: string; file: File }[];
};

export const buildCreateProductWithSkusPayload = (
  values: ProductSkusFormValues
): CreateProductWithSkusRequest => {
  const files: CreateProductWithSkusRequest['files'] = [];
  if (values.skuProductImage instanceof File) {
    files.push({ name: 'productImage', file: values.skuProductImage });
  }
  if (values.skuProductBarcodeImage instanceof File) {
    files.push({
      name: 'productBarcodeImage',
      file: values.skuProductBarcodeImage,
    });
  }
  const skus = values.skus.map((card, index) => {
    if (card.skuImage instanceof File) {
      files.push({ name: `skuImage_${index}`, file: card.skuImage });
    }
    if (card.skuBarcodeImage instanceof File) {
      files.push({
        name: `skuBarcodeImage_${index}`,
        file: card.skuBarcodeImage,
      });
    }
    return toPayloadSku(card);
  });
  return { payload: { product: toPayloadProduct(values), skus }, files };
};
